/**
 * OnboardingWizard - Wizard di onboarding per nuovi utenti
 * Guida l'utente nella compilazione dei dati aziendali e fiscali
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Card, CardContent } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Building2, 
  FileText, 
  MapPin, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Sparkles,
  Loader2,
  PartyPopper
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import AddressAutocomplete from './AddressAutocomplete';
import { VatLookupButton, VatLookupData } from './ui/vat-lookup-button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Province italiane
const PROVINCE_ITALIANE = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", 
  "BR", "BS", "BT", "BZ", "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ",
  "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE", "GO", "GR", "IM", "IS", "KR", "LC", "LE",
  "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT", "NA", "NO", "NU",
  "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA",
  "RC", "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV",
  "TA", "TE", "TN", "TO", "TP", "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

interface OnboardingData {
  // Step 1: Azienda
  company_name: string;
  display_name: string;
  // Step 2: Indirizzo
  address: string;
  city: string;
  postal_code: string;
  province: string;
  // Step 3: Dati Fiscali
  vat_number: string;
  tax_code: string;
  sdi_code: string;
  pec: string;
}

const STEPS = [
  { id: 1, title: 'Azienda', description: 'Informazioni base', icon: Building2 },
  { id: 2, title: 'Indirizzo', description: 'Sede legale', icon: MapPin },
  { id: 3, title: 'Dati Fiscali', description: 'Per fatturazione', icon: FileText },
  { id: 4, title: 'Completato', description: 'Tutto pronto!', icon: CheckCircle },
];

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
  onSkip?: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ open, onComplete, onSkip }) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingData, setExistingData] = useState<any>(null);
  
  const [formData, setFormData] = useState<OnboardingData>({
    company_name: '',
    display_name: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    vat_number: '',
    tax_code: '',
    sdi_code: '',
    pec: ''
  });

  // Carica dati esistenti
  useEffect(() => {
    if (open && user?.company_id) {
      loadExistingData();
    }
  }, [open, user?.company_id]);

  const loadExistingData = async () => {
    try {
      const [companyRes, fiscalRes] = await Promise.all([
        axios.get(`${API}/companies/my`),
        axios.get(`${API}/companies/my/fiscal`).catch(() => ({ data: { fiscal_data: {} } }))
      ]);
      
      const company = companyRes.data;
      const fiscal = fiscalRes.data.fiscal_data || {};
      
      setExistingData(company);
      setFormData({
        company_name: company.name || '',
        display_name: company.display_name || '',
        address: company.address || fiscal.address || '',
        city: company.city || fiscal.city || '',
        postal_code: company.postal_code || fiscal.postal_code || '',
        province: company.province || fiscal.province || '',
        vat_number: fiscal.vat_number || '',
        tax_code: fiscal.tax_code || '',
        sdi_code: fiscal.sdi_code || '',
        pec: fiscal.pec || ''
      });
    } catch (error) {
      console.error('Error loading existing data:', error);
    }
  };

  const handleChange = (field: keyof OnboardingData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Gestisce i dati ricevuti dal VAT Lookup
  const handleVatLookupData = (data: VatLookupData) => {
    const taxCode = data.tax_code || data.vat_code || '';
    const vatNumber = data.vat_code ? `IT${data.vat_code}` : '';
    
    setFormData(prev => ({
      ...prev,
      company_name: data.company_name || prev.company_name,
      vat_number: vatNumber || prev.vat_number,
      tax_code: taxCode || prev.tax_code,
      address: data.street || prev.address,
      city: data.city || prev.city,
      postal_code: data.postal_code || prev.postal_code,
      province: data.province || prev.province,
      pec: data.pec || prev.pec,
      sdi_code: data.sdi_code || prev.sdi_code,
    }));
    
    toast.success('Dati aziendali importati! Verifica e completa le informazioni.');
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return formData.company_name.trim().length > 0;
      case 2:
        return (
          formData.address.trim().length > 0 &&
          formData.city.trim().length > 0 &&
          formData.postal_code.trim().length === 5
        );
      case 3:
        // Almeno P.IVA o CF
        return formData.vat_number.length > 0 || formData.tax_code.length > 0;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Completa i campi obbligatori prima di procedere');
      return;
    }

    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Salva tutto
      await handleSave();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Salva dati azienda
      await axios.patch(`${API}/companies/my`, {
        name: formData.company_name,
        display_name: formData.display_name || formData.company_name,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        province: formData.province,
        country: 'IT'
      });

      // Salva dati fiscali
      await axios.put(`${API}/companies/my/fiscal`, {
        vat_number: formData.vat_number || null,
        tax_code: formData.tax_code || null,
        sdi_code: formData.sdi_code || null,
        pec: formData.pec || null
      });

      // Segna onboarding come completato
      await axios.patch(`${API}/auth/users/me`, {
        onboarding_completed: true
      });

      // Aggiorna stato locale
      updateUser({ onboarding_completed: true });

      // Mostra step finale
      setCurrentStep(4);
      
      toast.success('Dati salvati con successo!');
      
      // Auto-chiudi dopo 2 secondi
      setTimeout(() => {
        onComplete();
      }, 2500);
      
    } catch (error: any) {
      console.error('Error saving onboarding data:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipOnboarding = async () => {
    try {
      // Segna come completato anche se saltato
      await axios.patch(`${API}/auth/users/me`, {
        onboarding_completed: true
      });
      updateUser({ onboarding_completed: true });
      onSkip?.();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      onSkip?.();
    }
  };

  const progress = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header con Progress - fisso */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 pb-4 flex-shrink-0">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">Configura il tuo account</DialogTitle>
                <DialogDescription>
                  Completa i dati per iniziare a utilizzare la piattaforma
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {/* Step Indicator */}
          <div className="mt-4">
            <div className="flex justify-between mb-2">
              {STEPS.slice(0, -1).map((step, index) => (
                <div 
                  key={step.id}
                  className={cn(
                    "flex items-center gap-2 text-sm",
                    currentStep >= step.id ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all",
                    currentStep > step.id 
                      ? "bg-primary text-primary-foreground" 
                      : currentStep === step.id 
                        ? "bg-primary/20 text-primary border-2 border-primary"
                        : "bg-muted text-muted-foreground"
                  )}>
                    {currentStep > step.id ? <CheckCircle className="h-4 w-4" /> : step.id}
                  </div>
                  <span className="hidden sm:inline font-medium">{step.title}</span>
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        </div>

        {/* Content - scrollabile */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {currentStep === 1 && (
            <div className="space-y-4 animate-in fade-in-50 duration-300">
              <div className="text-center mb-6">
                <Building2 className="h-12 w-12 mx-auto text-primary/60 mb-2" />
                <h3 className="font-semibold text-lg">Informazioni Azienda</h3>
                <p className="text-sm text-muted-foreground">Come si chiama la tua azienda?</p>
              </div>
              
              {/* VAT Lookup Card */}
              <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-sm">Hai la Partita IVA?</p>
                      <p className="text-xs text-muted-foreground">
                        Compila automaticamente tutti i dati aziendali
                      </p>
                    </div>
                    <VatLookupButton 
                      onDataFound={handleVatLookupData}
                      variant="default"
                      size="sm"
                    />
                  </div>
                </CardContent>
              </Card>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    oppure compila manualmente
                  </span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="company_name">Ragione Sociale *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Es. Mario Rossi S.r.l."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="display_name">Nome Breve (opzionale)</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => handleChange('display_name', e.target.value)}
                    placeholder="Es. Mario Rossi"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Apparirà nella sidebar se la ragione sociale è troppo lunga
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4 animate-in fade-in-50 duration-300">
              <div className="text-center mb-6">
                <MapPin className="h-12 w-12 mx-auto text-primary/60 mb-2" />
                <h3 className="font-semibold text-lg">Sede Legale</h3>
                <p className="text-sm text-muted-foreground">Dove si trova la tua azienda?</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <AddressAutocomplete
                    label="Indirizzo *"
                    value={formData.address}
                    onChange={(value) => handleChange('address', value)}
                    onAddressSelect={(data) => {
                      handleChange('address', data.address);
                      if (data.city) handleChange('city', data.city);
                      if (data.postal_code) handleChange('postal_code', data.postal_code);
                      if (data.province) handleChange('province', data.province);
                    }}
                    placeholder="Es: Via Roma 15, Milano"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">Città *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Es. Milano"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="postal_code">CAP *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => handleChange('postal_code', e.target.value.slice(0, 5))}
                        placeholder="20100"
                        maxLength={5}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="province">Prov.</Label>
                      <Select 
                        value={formData.province} 
                        onValueChange={(value) => handleChange('province', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="--" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROVINCE_ITALIANE.map(prov => (
                            <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4 animate-in fade-in-50 duration-300">
              <div className="text-center mb-6">
                <FileText className="h-12 w-12 mx-auto text-primary/60 mb-2" />
                <h3 className="font-semibold text-lg">Dati Fiscali</h3>
                <p className="text-sm text-muted-foreground">Per emettere e ricevere fatture</p>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vat_number">Partita IVA</Label>
                    <Input
                      id="vat_number"
                      value={formData.vat_number}
                      onChange={(e) => handleChange('vat_number', e.target.value.toUpperCase())}
                      placeholder="IT12345678901"
                      maxLength={13}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tax_code">Codice Fiscale</Label>
                    <Input
                      id="tax_code"
                      value={formData.tax_code}
                      onChange={(e) => handleChange('tax_code', e.target.value.toUpperCase())}
                      placeholder="RSSMRA80A01H501Z"
                      maxLength={16}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <p className="text-sm font-medium mb-3">Fatturazione Elettronica (opzionale)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="sdi_code" className="text-xs">Codice SDI</Label>
                        <Input
                          id="sdi_code"
                          value={formData.sdi_code}
                          onChange={(e) => handleChange('sdi_code', e.target.value.toUpperCase())}
                          placeholder="0000000"
                          maxLength={7}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="pec" className="text-xs">PEC</Label>
                        <Input
                          id="pec"
                          type="email"
                          value={formData.pec}
                          onChange={(e) => handleChange('pec', e.target.value.toLowerCase())}
                          placeholder="azienda@pec.it"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <p className="text-xs text-muted-foreground text-center">
                  Inserisci almeno Partita IVA o Codice Fiscale
                </p>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="text-center py-8 animate-in fade-in-50 zoom-in-95 duration-500">
              <div className="relative inline-block">
                <div className="absolute inset-0 animate-ping">
                  <PartyPopper className="h-16 w-16 text-primary/30" />
                </div>
                <PartyPopper className="h-16 w-16 text-primary relative" />
              </div>
              <h3 className="font-bold text-2xl mt-6 mb-2">Perfetto!</h3>
              <p className="text-muted-foreground mb-4">
                Il tuo account è configurato e pronto all'uso.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-primary">
                <CheckCircle className="h-4 w-4" />
                <span>Dati salvati con successo</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer - fisso in basso */}
        {currentStep < 4 && (
          <div className="border-t p-4 bg-muted/30 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 1 ? (
                  <Button 
                    variant="ghost" 
                    onClick={handleBack}
                    disabled={loading}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Indietro
                  </Button>
                ) : (
                  <Button 
                    variant="ghost" 
                    onClick={handleSkipOnboarding}
                    className="text-muted-foreground"
                  >
                    Salta per ora
                  </Button>
                )}
              </div>
              
              <Button 
                onClick={handleNext}
                disabled={loading || !validateStep(currentStep)}
                className="gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvataggio...
                  </>
                ) : currentStep === 3 ? (
                  <>
                    Completa
                    <CheckCircle className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Continua
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
